import { debounce } from "lodash";
import { useEffect, useState, useCallback, ChangeEvent, FocusEvent } from "react";
import { IPartnerUpdate } from "../domain/IPartnerUpdate";
import { partnersService } from "../services/partner.service";
import { updatePartner } from "../scenes/Main/main.slice";
import { IPartner } from "../domain";
import { AppDispatch } from "store";
import { useActivities } from "../scenes/Main/hooks/Activities";
import { useMunicipalities } from "../scenes/Main/hooks/Municipalities";
import { toast } from "react-toastify";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasWriteAccessTo } from "utils/authUtil";
import { PARTNER_MODULE_NAME } from "Dashboard/scenes/Partners/domain/PartnerModule";
import { useSelector } from "react-redux";

interface IProps {
  selectedPartner: IPartner;
  dispatch: AppDispatch;
}

const PartnerInfoToolTip = ({ selectedPartner, dispatch }: IProps) => {
  const [partner, setPartner] = useState<IPartnerUpdate>({
    partnerId: selectedPartner?.partnerId,
    id: selectedPartner?.id,
    identificationNumber: selectedPartner?.identificationNumber,
    activityId: selectedPartner?.activity?.activityId,
    address: selectedPartner?.address,
    bankAccount: selectedPartner?.account,
    fullName: selectedPartner?.fullName,
    shortName: selectedPartner?.shortName,
    municipalityId: selectedPartner?.municipality?.municipalityId,
    phones: selectedPartner?.phones,
    note: selectedPartner?.note,
  });
  const [bankAccount, setBankAccount] = useState("");
  const [bankAccountError, setBankAccountError] = useState<string | undefined>("");
  const [identificationNumber, setIdentificationNumber] = useState("");
  const [identificationNumberInFocus, setIdentificationNumberInFocus] = useState(false);
  const { user } = useSelector(selectStateAuth);


  const activities = useActivities();
  const municipalities = useMunicipalities();

  const delayedQueryPartner = useCallback(
    debounce(async (data) => {
      try {
        var partner = await partnersService.updatePartner(data);
        dispatch(updatePartner(partner));
        toast.success("Saradnik je uspešno izmenjen.");
      } catch (err: any) {
        if (err.response.status === 409) {
          const errorMessage: string = err.response.data.message;
          const pattern = "other partner: ";
          const otherPartner = errorMessage.substring(errorMessage.indexOf(pattern) + pattern.length, errorMessage.length - 1);
          toast.error(`PIB je zauzet (${otherPartner})`);
        } else {
          toast.error("Doslo je do nepoznate greške");

        }
      }
    }, 1000),
    []
  );

  const updateFieldChanged = (
    event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    let newValue: number | string | null;
    if (name === "id") {
      if (value === "") {
        newValue = null;
      } else {
        newValue = parseInt(value);
      }
    } else if (name === "identificationNumber") {
      setIdentificationNumber(value);
      newValue = value;
    } else if (name === "bankAccount") {
      newValue = validateAndFormatBankAccount(value, false, true, false);
    } else if (name === "activityId" || name === "municipalityId") {
      if (value === "-1") {
        newValue = null;
      } else {
        newValue = parseInt(value);
      }
    } else {
      newValue = value;
    }
    let newPartner = { ...partner, [name]: newValue };
    delayedQueryPartner(newPartner);
    setPartner(newPartner);
  };

  const validateAndFormatBankAccount = (inputValue: string, format: boolean, showError: boolean, forbidChanging: boolean): string | null => {
    if (!showError) {
      setBankAccountError(undefined);
    }
    if (inputValue === "") {
      setBankAccount("");
      setBankAccountError(undefined);
      return null;
    }

    const cleanedInputValue = inputValue.replace(/[^\d]/g, "");
    const regexPattern = /^(\d{3})(\d{0,13})(\d{2})$/;
    const matches = cleanedInputValue.match(regexPattern);

    if (!matches) {
      setBankAccount(cleanedInputValue);
      if (showError) {
        setBankAccountError("Neispravan format broja računa");
      }
      return cleanedInputValue;
    }

    let bankCodeRaw = matches[1];
    let accountNumberRaw = matches[2];
    let controlNumberRaw = matches[3];

    if (bankCodeRaw === "" || accountNumberRaw === "" || controlNumberRaw === "") {
      setBankAccount(cleanedInputValue);
      if (showError) {
        setBankAccountError("Neispravan format broja računa");
      }
      return cleanedInputValue;
    }
    let bankCode = BigInt(bankCodeRaw);
    let accountNumber = BigInt(accountNumberRaw);
    let controlNumber = BigInt(controlNumberRaw);
    if (((bankCode * BigInt(10000000000000) + accountNumber) * BigInt(100) + controlNumber) % BigInt(97) !== BigInt(1)) {
      setBankAccount(cleanedInputValue);
      if (showError) {
        setBankAccountError("Neispravan kontrolni broj računa");
      }
      return cleanedInputValue;
    }
    const formattedValue = [bankCodeRaw, accountNumberRaw, controlNumberRaw].join(format ? "-" : "");

    setBankAccount(formattedValue);
    setBankAccountError(undefined);
    return [bankCodeRaw, accountNumberRaw, controlNumberRaw].join("-");
  }


  const handleBankAccountFocusIn = (event: FocusEvent<HTMLInputElement, Element>) => {
    const inputValue = event.target.value;
    validateAndFormatBankAccount(inputValue, false, true, false);

  }

  const handleBankAccountFocusOut = (event: FocusEvent<HTMLInputElement, Element>) => {
    const inputValue = event.target.value;
    validateAndFormatBankAccount(inputValue, true, true, false);
  }

  const handleIdentificationNumberFocusIn = (event: FocusEvent<HTMLInputElement, Element>) => {
    setIdentificationNumberInFocus(true);
  }

  const handleIdentificationNumberFocusOut = (event: FocusEvent<HTMLInputElement, Element>) => {
    setIdentificationNumberInFocus(false);
    setIdentificationNumber(partner?.identificationNumber);
  }


  const onChangeTA = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let newValue: string | string[];
    if (name === "phones") {
      newValue = value.split(/\s+/);
    } else {
      newValue = value;
    }
    let newPartner = { ...partner, [name]: newValue };
    delayedQueryPartner(newPartner);
    setPartner(newPartner);
  };

  useEffect(() => {
    setPartner({
      partnerId: selectedPartner?.partnerId,
      id: selectedPartner?.id,
      identificationNumber: selectedPartner?.identificationNumber,
      activityId: selectedPartner?.activity?.activityId,
      address: selectedPartner?.address,
      bankAccount: selectedPartner?.account,
      fullName: selectedPartner?.fullName,
      shortName: selectedPartner?.shortName,
      municipalityId: selectedPartner?.municipality?.municipalityId,
      phones: selectedPartner?.phones,
      note: selectedPartner?.note,
    });
    if (!identificationNumberInFocus) {
      setIdentificationNumber(selectedPartner?.identificationNumber);
    }
  }, [selectedPartner, identificationNumberInFocus]);

  useEffect(() => {
    setBankAccount(selectedPartner?.account);
    setIdentificationNumber(selectedPartner?.identificationNumber);
  }, [selectedPartner?.account, selectedPartner?.identificationNumber]);

  return (
    <>
      {partner && (
        <div className="partner-info">
          <input
            type="text"
            value={partner.shortName || ''}
            name="shortName"
            className="title"
            onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? updateFieldChanged : undefined}
            disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
          />
          <div className="row">
            <span>Ime za fakturu</span>
            <input
              type="text"
              value={partner.fullName || ''}
              name="fullName"
              onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? updateFieldChanged : undefined}
              disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
            />
          </div>
          <div className="row">
            <span>delatnost</span>
            <select
              className="select w70 center"
              name="activityId"
              value={partner.activityId || -1}
              onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? updateFieldChanged : undefined}
              disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
            >
              <option key={-1} value={-1}>
                {"Izaberi"}
              </option>
              {activities.map((activity) => (
                <option key={activity.activityId} value={activity.activityId}>
                  {activity.name}
                </option>
              ))}
            </select>
          </div>
          <div className="row">
            <span>br. racuna</span>
            <input
              type="text"
              value={bankAccount}
              name="bankAccount"
              onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? updateFieldChanged : undefined}
              onFocus={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? handleBankAccountFocusIn : undefined}
              onBlur={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? handleBankAccountFocusOut : undefined}
              disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
            />
          </div>
          {bankAccountError && (
            <div className="row"><span>{bankAccountError}</span></div>
          )}
          <div className="row">
            <span>br.lk./PIB</span>
            <input
              type="text"
              name="id"
              value={partner.id || ""}
              onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? updateFieldChanged : undefined}
              disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
            />
          </div>
          <div className="row">
            <span>matični broj</span>
            <input
              type="text"
              name="identificationNumber"
              value={identificationNumber || ""}
              onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? updateFieldChanged : undefined}
              onFocus={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? handleIdentificationNumberFocusIn : undefined}
              onBlur={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? handleIdentificationNumberFocusOut : undefined}
              disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
            />
          </div>
          <div className="row">
            <span>sup/mesto</span>
            <select
              className="select w70 center"
              name="municipalityId"
              value={partner.municipalityId || ""}
              onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? updateFieldChanged : undefined}
              disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
            >
              <option key={-1} value={-1}>
                {"Izaberi"}
              </option>
              {municipalities.map((municipality) => (
                <option
                  key={municipality.municipalityId}
                  value={municipality.municipalityId}
                >
                  {municipality.name}
                </option>
              ))}
            </select>
          </div>
          <div className="row">
            <span>telefoni</span>
            <textarea
              rows={2}
              value={partner.phones != null ? partner.phones.join("\n") : ""}
              name="phones"
              onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? onChangeTA : undefined}
              disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
            />
          </div>
          <div className="row">
            <span>adresa</span>
            <input
              type="text"
              name="address"
              value={partner.address || ''}
              onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? updateFieldChanged : undefined}
              disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
            />
          </div>
          <br />
          <textarea
            rows={3}
            placeholder="Napomena"
            value={partner.note}
            name="note"
            onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? onChangeTA : undefined}
            disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
          ></textarea>
        </div>
      )}
    </>
  );
};

export default PartnerInfoToolTip;
